import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';
import LoadingOverlay from '../LoadingOverlay';
import Toast from '../Toast';
import ConfirmDialog from '../ConfirmDialog';
import { normalizeRestaurantId } from '../../utils/restaurantUtils';

const WaiterTableManagement = ({ waiterId, restaurantId: restaurantIdProp, onNavigate }) => {
    // Normalize restaurantId to ensure it's always a string
    const restaurantId = normalizeRestaurantId(restaurantIdProp);
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [selectedTable, setSelectedTable] = useState(null);
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => {},
        confirmText: 'Confirm',
        confirmColor: 'blue'
    });

    useEffect(() => {
        if (waiterId && restaurantId) {
            fetchTables();
        }
    }, [waiterId, restaurantId]);

    const fetchTables = async () => {
        try {
            setLoading(true);
            // Fetch all tables for the restaurant, not just assigned ones
            const response = await axios.get(API_ENDPOINTS.TABLES(restaurantId));
            setTables(response.data || []);
        } catch (error) {
            console.error('Failed to fetch tables:', error);
            showToast('Failed to load tables', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const handleStatusChange = (table, newStatus) => {
        setConfirmDialog({
            isOpen: true,
            title: `Mark Table ${table.tableNumber} as ${newStatus}`,
            message: `Are you sure you want to mark table ${table.tableNumber} as ${newStatus}?`,
            confirmText: 'Confirm',
            confirmColor: 'blue',
            onConfirm: async () => {
                setActionLoading(true);
                setLoadingMessage(`Updating table status...`);
                try {
                    await axios.put(API_ENDPOINTS.TABLE_STATUS_WAITER(table._id), {
                        status: newStatus,
                        waiterId
                    });
                    showToast(`Table ${table.tableNumber} marked as ${newStatus}`);
                    await fetchTables();
                } catch (error) {
                    showToast(error.response?.data?.message || 'Failed to update table status', 'error');
                } finally {
                    setActionLoading(false);
                }
            }
        });
    };

    const handleOpenTable = (table) => {
        setSelectedTable(table);
        setShowOrderModal(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Available':
                return 'bg-green-100 text-green-800 border-green-300';
            case 'Occupied':
                return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            case 'Reserved':
                return 'bg-orange-100 text-orange-800 border-orange-300';
            case 'Blocked':
                return 'bg-red-100 text-red-800 border-red-300';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-300';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'Available':
                return '🟢';
            case 'Occupied':
                return '🟡';
            case 'Reserved':
                return '🟠';
            case 'Blocked':
                return '🔴';
            default:
                return '⚪';
        }
    };

    const getActiveOrderCount = (table) => {
        if (table.currentOrderId && Array.isArray(table.currentOrderId)) {
            return table.currentOrderId.filter(order => 
                order.status && !['CLOSED', 'CANCELLED'].includes(order.status)
            ).length;
        }
        return table.currentOrderId ? 1 : 0;
    };

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
        );
    }

    return (
        <div className="relative">
            {actionLoading && <LoadingOverlay message={loadingMessage} />}

            <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2">Table Management</h2>
                <p className="text-gray-600">View all tables and manage their status (Available/Occupied)</p>
            </div>

            {tables.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <span className="text-6xl block mb-4">🪑</span>
                    <p className="text-gray-600 text-lg">No tables found</p>
                    <p className="text-gray-500 text-sm mt-2">Contact your manager to add tables</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {tables.map((table) => {
                        const activeOrders = getActiveOrderCount(table);
                        return (
                            <div
                                key={table._id}
                                className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500 hover:shadow-lg transition-shadow"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-800">
                                            Table {table.tableNumber}
                                        </h3>
                                        {table.tableName && table.tableName !== table.tableNumber && (
                                            <p className="text-sm text-gray-500">{table.tableName}</p>
                                        )}
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(table.status)}`}>
                                        {getStatusIcon(table.status)} {table.status}
                                    </span>
                                </div>

                                {activeOrders > 0 && (
                                    <div className="mb-4 bg-blue-50 rounded-lg p-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-blue-600 font-semibold">Active Orders:</span>
                                            <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                                                {activeOrders}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {table.occupiedAt && (
                                    <p className="text-xs text-gray-500 mb-4">
                                        Occupied since: {new Date(table.occupiedAt).toLocaleTimeString()}
                                    </p>
                                )}

                                {table.assignedWaiterId && (
                                    <div className="mb-3 text-xs text-gray-500">
                                        {table.assignedWaiterId.toString() === waiterId 
                                            ? '✅ Assigned to you' 
                                            : '👤 Assigned to another waiter'}
                                    </div>
                                )}
                                
                                <div className="flex flex-wrap gap-2">
                                    {table.status === 'Available' ? (
                                        <button
                                            onClick={() => handleStatusChange(table, 'Occupied')}
                                            className="flex-1 bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors text-sm font-medium"
                                        >
                                            Mark Occupied
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleStatusChange(table, 'Available')}
                                            className="flex-1 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                                        >
                                            Mark Available
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleOpenTable(table)}
                                        className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                                    >
                                        View Orders
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Order Modal - This will be handled by parent or navigate to order screen */}
            {showOrderModal && selectedTable && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-xl font-bold mb-4">Table {selectedTable.tableNumber}</h3>
                        <p className="text-gray-600 mb-6">
                            Opening order management for this table...
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowOrderModal(false);
                                    setSelectedTable(null);
                                    // Navigate to order management tab
                                    if (onNavigate) {
                                        onNavigate('orders');
                                    }
                                }}
                                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Go to Orders
                            </button>
                            <button
                                onClick={() => {
                                    setShowOrderModal(false);
                                    setSelectedTable(null);
                                }}
                                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {toast.show && <Toast message={toast.message} type={toast.type} />}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
                onConfirm={confirmDialog.onConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
                confirmText={confirmDialog.confirmText}
                confirmColor={confirmDialog.confirmColor}
            />
        </div>
    );
};

export default WaiterTableManagement;

